import { SetupStatus, type Settings } from '@deizi/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Field, Seg } from '../../components/Form';
import { Alert } from '../../components/States';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { useUpdateSettings } from '../../lib/settings-api';
import { setupKey } from '../../lib/setup-api';
import { LogoCard } from '../settings/business/BrandCards';
import { StepHead, WizardFooter } from './WizardParts';

const text = (v: string) => (v.trim() === '' ? null : v.trim());

/** Step 1 · Sobre o negócio (optional). */
export function StepBusiness({ settings, onNext }: { settings: Settings; onNext: () => void }) {
  const b = settings.business;
  const [form, setForm] = useState({
    shopName: b.shopName ?? 'Deizi3d',
    cnpj: b.cnpj ?? '',
    regime: b.regime === 'simples' ? 'simples' : 'mei',
    city: b.city ?? 'Florianópolis · SC',
    whatsapp: b.whatsapp ?? '',
    contactEmail: b.contactEmail ?? '',
    instagram: b.instagram ?? '',
  } as const satisfies Record<string, string>);
  const update = useUpdateSettings();
  const qc = useQueryClient();
  const skip = useMutation({
    mutationFn: () => api.post('/setup/skip-business', SetupStatus),
    onSuccess: (s) => {
      qc.setQueryData(setupKey, s);
      onNext();
    },
  });
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const grid = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit,minmax(min(220px,100%),1fr))',
    gap: 14,
  } as const;

  return (
    <>
      <StepHead title="Sobre o negócio">
        Opcional. Aparece nos orçamentos em PDF e nos recibos; dá para completar depois.
      </StepHead>
      <div style={grid}>
        <Field id="w-shop" label="Nome da loja">
          <input
            id="w-shop"
            className="input"
            value={form.shopName}
            onChange={(e) => set('shopName', e.target.value)}
          />
        </Field>
        <Field id="w-cnpj" label="CNPJ" help="O MEI tem CNPJ. Fica no recibo e na nota.">
          <input
            id="w-cnpj"
            className="input"
            placeholder="00.000.000/0001-00"
            value={form.cnpj}
            onChange={(e) => set('cnpj', e.target.value)}
          />
        </Field>
      </div>
      <Field label="Regime tributário" help="Define como o imposto entra no passo 2.">
        <Seg
          label="Regime tributário"
          value={form.regime}
          onChange={(v) => set('regime', v)}
          options={[
            { value: 'mei', label: 'MEI' },
            { value: 'simples', label: 'Simples Nacional' },
          ]}
        />
      </Field>
      <div style={grid}>
        <Field id="w-city" label="Cidade">
          <input
            id="w-city"
            className="input"
            value={form.city}
            onChange={(e) => set('city', e.target.value)}
          />
        </Field>
        <Field id="w-wa" label="WhatsApp">
          <input
            id="w-wa"
            className="input"
            placeholder="(48) 9 ____-____"
            value={form.whatsapp}
            onChange={(e) => set('whatsapp', e.target.value)}
          />
        </Field>
        <Field id="w-mail" label="E-mail">
          <input
            id="w-mail"
            type="email"
            autoComplete="off"
            className="input"
            value={form.contactEmail}
            onChange={(e) => set('contactEmail', e.target.value)}
          />
        </Field>
        <Field id="w-ig" label="Instagram">
          <input
            id="w-ig"
            className="input"
            placeholder="@perfil"
            value={form.instagram}
            onChange={(e) => set('instagram', e.target.value)}
          />
        </Field>
      </div>
      <LogoCard readOnly={false} />
      {update.isError || skip.isError ? (
        <Alert tone="err">{errorMessage(update.error ?? skip.error)}</Alert>
      ) : null}
      <WizardFooter
        busy={update.isPending || skip.isPending}
        onSkip={() => skip.mutate()}
        onContinue={() =>
          update.mutate(
            {
              business: {
                shopName: text(form.shopName),
                cnpj: text(form.cnpj),
                regime: form.regime,
                city: text(form.city),
                whatsapp: text(form.whatsapp),
                contactEmail: text(form.contactEmail),
                instagram: text(form.instagram),
              },
            },
            { onSuccess: onNext },
          )
        }
      />
    </>
  );
}
